import {
  Component,
  ChangeDetectorRef,
  OnInit,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  AfterViewChecked,
} from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, filter, BehaviorSubject } from 'rxjs';
import { Project } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { PanelsInterfaceComponent, PanelsInterfaceComponentBase } from '../panels-interface.component';
import clone from 'lodash-es/clone';
import isEmpty from 'lodash-es/isEmpty';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { FieldDef, ProjectPhase, ProjectStage } from 'app/core/models';
import { ProjectStatusRequiredFieldsComponent } from 'app/modules/projects/modals/project-status-required-fields/project-status-required-fields.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { getAllowedFields } from 'app/shared/functions';
import { RelatedToType } from 'app/shared/enums';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { calendarColors } from 'app/modules/calendar/sidebar/calendar-colors';
import cloneDeep from 'lodash-es/cloneDeep';
import { finalize } from 'rxjs/operators';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';

const IDLE_TIME_TO_SAVE = 60000;

@Component({
  selector: 'project-panel-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsFormComponent
  extends PanelsInterfaceComponentBase
  implements PanelsInterfaceComponent, OnInit, AfterViewChecked
{
  private defaultFieldDefs: FieldDef[] = [];
  public fields: any[] = [];
  public fieldsCombined: any[] = [];
  private _originalFields: any[] = [];

  fieldDefs: Array<FieldDef> = [];
  rowActionDisabled = [];
  projectStages: ProjectStage[];
  projectPhases: ProjectPhase[];
  headerColors: any;
  autoSave: Subject<any> = new Subject<any>();
  changesToSave = {};
  prevProject: Project;
  savingChanges = new BehaviorSubject(false);

  constructor(
    private _projectsService: ProjectsService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private _matDialog: MatDialog,
  ) {
    super(_changeDetectorRef);
    this.headerColors = ['None', ...calendarColors];
  }

  private get isRolePanel(): boolean {
    return this.panelDef.type === 'roles';
  }

  ngOnInit() {
    this.autoSave
      .pipe(
        debounceTime(IDLE_TIME_TO_SAVE),
        distinctUntilChanged(),
        filter(() => this.hasPendingChanges),
        untilComponentDestroyed(this),
      )
      .subscribe(() => {
        this.saveChanges();
      });

    this.cleanPanels();
    const canEditDeal = this.project?.permissions?.is_editable;
    this.readonly = !canEditDeal || !!this.project?.archived_at;
    this.fields = clone(this.panelDef.meta.fields);
    this._originalFields = clone(this.panelDef.meta.fields);
    this.titleFieldDef = {
      name: 'form.panel_name',
      label: this.panelDef.title,
      type: 'text',
    };
    this.panelColor = this.panelDef?.color;
    this.fieldDefs = getAllowedFields(this.data?.fieldDefs, RelatedToType.Project);
    this.data.fieldDefs = this.fieldDefs;
    this.projectStages = this.data.options.project_stages.filter((option) => !option.hidden);
    this.projectPhases = this.data.options.project_phases.filter((option) => !option.hidden);
    this._projectsService.project$.pipe(untilComponentDestroyed(this)).subscribe((project: Project) => {
      if (project) {
        this.project = cloneDeep(project);
        this.prevProject = cloneDeep(project);
      }
    });

    if (this.isRolePanel) {
      this.store
        .select(
          ObjectsManagerState.getFieldsByObjectName(RelatedToType.Project, {
            addNestedFields: false,
            allowedFieldsOnly: true,
          }),
        )
        .pipe(untilComponentDestroyed(this))
        .subscribe((fields) => {
          if (fields) {
            this.defaultFieldDefs = fields.filter(
              (fItem) => fItem.task_role_id !== null && fItem.task_role_id !== undefined,
            );
            this.fields = this.defaultFieldDefs.map((item) => [item.name]);
          }
        });
    }

    this.store
      .select(
        ObjectsManagerState.getFieldsByObjectName(RelatedToType.Project, {
          addNestedFields: false,
          allowedFieldsOnly: true,
        }),
      )
      .pipe(untilComponentDestroyed(this))
      .subscribe((fields) => {
        if (fields) this.defaultFieldDefs = fields;
      });
  }

  ngAfterViewChecked(): void {
    this._changeDetectorRef.detectChanges();
  }

  cleanPanels() {
    let cleanFields = [];
    this.panelDef.meta.fields.forEach((field) => {
      let cleanField = field.filter(function (e) {
        return e;
      });
      if (cleanField.length > 0)
        cleanFields.push(
          field.filter(function (e) {
            return e;
          }),
        );
    });

    this.panelDef.meta.fields = cleanFields;
    this.fields = clone(this.panelDef.meta.fields);
  }

  createFieldList() {
    this.fieldsCombined = [];
    const leftCol = [],
      rightCol = [];
    this.fields.map((field) => {
      leftCol.push(field[0]);
      rightCol.push(field[1]);
    });
    this.fieldsCombined.push(leftCol);
    this.fieldsCombined.push(rightCol);
  }

  dropped(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  toggleEditMode(editMode: boolean | null = null): void {
    this.cleanPanels();

    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }

    if (this.editMode === false) {
      this.panelName = null;
      this.project[this.titleFieldDef.name] = null;
    }

    this._changeDetectorRef.markForCheck();
  }

  cancelEdits(): void {
    this.fields = this._originalFields;

    this.panelDef.meta.fields = clone(this._originalFields);

    this.toggleEditMode(false);
  }

  saveLayout(): void {
    this.panelDef.meta.fields = this.fields.filter((row) => row && row.length > 0);
    if (this.panelName) {
      this.panelDef.title = this.panelName;
      this.titleFieldDef.label = this.panelName;
    }

    if (this.panelColor) {
      this.panelDef.color = this.panelColor;
    } else {
      this.panelDef.color = null;
    }

    this.onEditPanelDef.emit(this.panelDef);
    this.toggleEditMode(false);
  }

  onFieldSelectionChange($event, row, col): void {
    this.fields[row][col] = $event[this.fields[row][col]] || this.fields[row][col];
  }

  removeField(row, col): void {
    this.fields[row].splice(col, 1);
    if (isEmpty(this.fields[row])) {
      this.fields.splice(row, 1);
    }
    this._changeDetectorRef.markForCheck();
  }

  addField(row: number, lastCol: number): void {
    const filterFields: FieldDef[] = this.data.fieldDefs.filter(
      (value: FieldDef) => !this.fields[row].includes(value.name),
    );

    let fieldToBeAdded = 'id';

    if (filterFields.length > 0) fieldToBeAdded = filterFields[0].name;

    this.fields[row].push(fieldToBeAdded);

    this._changeDetectorRef.markForCheck();
  }

  addRow(index: number): void {
    if (this.fields.length > 0) {
      this.fields.splice(index + 1, 0, ['id']);
      this.panelDef.meta.fields.splice(index + 1, 0, ['id']);
    } else {
      this.fields.push(['id']);
      this.panelDef.meta.fields.push(['id']);
    }
    this._changeDetectorRef.markForCheck();
  }

  get hasPendingChanges(): boolean {
    return Object.keys(this.changesToSave).filter((key) => key !== 'id').length > 0;
  }

  pendingToSave(fieldName) {
    return this.changesToSave[fieldName];
  }

  saveChanges() {
    this.savingChanges.next(true);

    this._projectsService
      .saveProject(this.changesToSave, this.project)
      .pipe(
        untilComponentDestroyed(this),
        finalize(() => this.savingChanges.next(false)),
      )
      .subscribe(() => {
        this.prevProject = clone({ ...this.project, ...this.changesToSave });
        this.changesToSave = {};
        this.reloadAllPanelsOfTypes?.emit([this.panelDef.type]);
        this.changeDetectorRef.detectChanges();
      });
  }

  discardChanges() {
    this.toggleEditMode(true);
    this.changesToSave = {};
    this.project = clone(this.prevProject);
    this.toggleEditMode(false);
  }

  update(params) {
    let currentKey = Object.keys(params)[0];
    let options = { ...params, id: this.project?.id };
    if (currentKey === this.titleFieldDef.name) {
      this.panelName = params[this.titleFieldDef.name];
    } else {
      const selectColumn = this.defaultFieldDefs.map((m) => m.name) || [];
      const paramsKeys = Object.keys(params);
      selectColumn.push(currentKey);
      let hasRequiredFields = false;
      if (paramsKeys.includes('project_stage_id')) {
        const stage = this.projectStages.find((option: ProjectStage) => option.id === Number(params.project_stage_id));
        hasRequiredFields = this.handleSoftRequiredFields(stage, selectColumn);
      } else if (paramsKeys.includes('project_phase_id')) {
        const phase = this.projectPhases.find(({ id }: ProjectPhase) => id === Number(params.project_phase_id));
        hasRequiredFields = this.handleSoftRequiredFields(phase, selectColumn, false);
      }
      if (!hasRequiredFields) {
        this.changesToSave = { ...this.changesToSave, ...options };

        if (this.prevProject[currentKey] === this.changesToSave[currentKey]) {
          delete this.changesToSave[currentKey];
          this.changeDetectorRef.detectChanges();
        }
        this.autoSave.next(this.changesToSave);
      }
    }
  }

  lookupField(name) {
    let fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }

  chunk(array, size): any[] {
    const chunkedArray = [];
    let copied = [...array];
    // Round up to the nearest integer
    const numOfChild = Math.ceil(copied.length / size);
    for (let i = 0; i < numOfChild; i++) {
      chunkedArray.push(copied.splice(0, size));
    }
    return chunkedArray;
  }

  allowAddNewColumn(event, rowIndex) {
    this.rowActionDisabled[rowIndex] = event;
  }

  private handleSoftRequiredFields(entity: ProjectStage | ProjectPhase, columns: string[], isStatus = true): boolean {
    if (entity?.required_fields) {
      this._matDialog
        .open(ProjectStatusRequiredFieldsComponent, {
          disableClose: true,
          panelClass: 'mailbox-compose-dialog',
          data: { data: this.data, columns, stage: entity, project: cloneDeep(this.project), isStatus },
        })
        .afterClosed()
        .subscribe((modalResponse: { updatedProject: boolean }) => {
          if (!modalResponse?.updatedProject) {
            this.toggleEditMode(true);
            const field = isStatus ? 'project_stage_id' : 'project_phase_id';
            delete this.changesToSave[field];

            if (isStatus) {
              const prevStage = this.projectStages.find(
                (option: ProjectStage) => option.id === Number(this.prevProject[field]),
              );

              this.project = cloneDeep({
                ...this.project,
                ...this.changesToSave,
                project_stage: prevStage,
                [field]: this.prevProject[field],
              });
            } else {
              const prevPhase = this.projectPhases.find(
                (option: ProjectStage) => option.id === Number(this.prevProject[field]),
              );

              this.project = cloneDeep({
                ...this.project,
                ...this.changesToSave,
                project_phase: prevPhase,
                [field]: this.prevProject[field],
              });
            }

            this.toggleEditMode(false);
          }
        });
      return true;
    }
    return false;
  }
}
